import { Link } from 'react-router-dom';
import { useCart } from '@/lib/CartContext';
import { formatPrice } from '@/lib/api';
import SEOHead from '@/components/SEOHead';
import { Minus, Plus, Trash2, ShoppingCart, ArrowLeft, ArrowRight } from 'lucide-react';

export default function CartPage() {
    const { items, removeItem, updateQuantity, total, itemCount, clearCart } = useCart();

    if (items.length === 0) {
        return (
            <>
                <SEOHead title="Carrito" />
                <div className="max-w-3xl mx-auto px-4 py-20 text-center" data-testid="empty-cart">
                    <div className="inline-flex items-center justify-center w-20 h-20 bg-cp-surface rounded-full mb-6">
                        <ShoppingCart size={32} className="text-cp-text-muted" />
                    </div>
                    <h1 className="text-2xl font-heading font-bold text-cp-text-main mb-3">Tu carrito esta vacio</h1>
                    <p className="text-cp-text-body mb-6">Agrega productos para comenzar tu compra</p>
                    <Link
                        to="/productos"
                        data-testid="continue-shopping-link"
                        className="inline-flex items-center gap-2 bg-cp-secondary hover:bg-cp-secondary-hover text-white px-6 py-3 rounded-xl font-semibold text-sm transition-all"
                    >
                        Ver Productos <ArrowRight size={16} />
                    </Link>
                </div>
            </>
        );
    }

    return (
        <>
            <SEOHead title="Carrito de Compras" />
            <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8 lg:py-12" data-testid="cart-page">
                <h1 className="text-2xl sm:text-3xl font-heading font-bold text-cp-text-main mb-8">
                    Carrito de Compras ({itemCount} {itemCount === 1 ? 'producto' : 'productos'})
                </h1>

                <div className="grid lg:grid-cols-3 gap-8">
                    {/* Items */}
                    <div className="lg:col-span-2 space-y-4">
                        {items.map(item => (
                            <div
                                key={item.product_id}
                                data-testid={`cart-item-${item.product_id}`}
                                className="flex gap-4 bg-white border border-cp-border rounded-2xl p-4"
                            >
                                <div className="w-20 h-20 sm:w-24 sm:h-24 bg-cp-surface-soft rounded-xl overflow-hidden shrink-0">
                                    {item.image ? (
                                        <img src={item.image} alt={item.product_name} className="w-full h-full object-cover" />
                                    ) : (
                                        <div className="w-full h-full flex items-center justify-center text-cp-text-muted text-xs">Sin img</div>
                                    )}
                                </div>
                                <div className="flex-1 min-w-0">
                                    <h3 className="font-heading font-bold text-cp-text-main text-sm sm:text-base truncate">{item.product_name}</h3>
                                    <p className="text-cp-brand font-bold mt-1">{formatPrice(item.price)}</p>
                                    <div className="flex items-center justify-between mt-3">
                                        <div className="flex items-center border border-cp-border rounded-lg overflow-hidden">
                                            <button
                                                onClick={() => updateQuantity(item.product_id, item.quantity - 1)}
                                                className="px-2 py-1.5 text-cp-text-body hover:bg-cp-bg-alt"
                                                disabled={item.quantity <= 1}
                                            >
                                                <Minus size={14} />
                                            </button>
                                            <span className="px-3 py-1.5 text-sm font-semibold">{item.quantity}</span>
                                            <button
                                                onClick={() => updateQuantity(item.product_id, item.quantity + 1)}
                                                className="px-2 py-1.5 text-cp-text-body hover:bg-cp-bg-alt"
                                            >
                                                <Plus size={14} />
                                            </button>
                                        </div>
                                        <div className="flex items-center gap-4">
                                            <span className="text-sm font-bold text-cp-text-main">{formatPrice(item.price * item.quantity)}</span>
                                            <button
                                                onClick={() => removeItem(item.product_id)}
                                                data-testid={`remove-item-${item.product_id}`}
                                                className="text-cp-text-muted hover:text-cp-error transition-colors"
                                            >
                                                <Trash2 size={16} />
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        ))}
                        <button
                            onClick={clearCart}
                            data-testid="clear-cart"
                            className="text-sm text-cp-text-muted hover:text-cp-error transition-colors"
                        >
                            Vaciar carrito
                        </button>
                    </div>

                    {/* Summary */}
                    <div className="lg:col-span-1">
                        <div className="bg-white border border-cp-border rounded-2xl p-6 sticky top-24" data-testid="cart-summary">
                            <h2 className="font-heading font-bold text-cp-text-main text-lg mb-4">Resumen</h2>
                            <div className="space-y-3 mb-6">
                                <div className="flex justify-between text-sm">
                                    <span className="text-cp-text-body">Subtotal ({itemCount} productos)</span>
                                    <span className="font-semibold text-cp-text-main">{formatPrice(total)}</span>
                                </div>
                                <div className="flex justify-between text-sm">
                                    <span className="text-cp-text-body">Envio</span>
                                    <span className="text-cp-success font-semibold">A coordinar</span>
                                </div>
                                <div className="border-t border-cp-border-soft pt-3 flex justify-between">
                                    <span className="font-heading font-bold text-cp-text-main">Total</span>
                                    <span className="font-heading font-bold text-xl text-cp-brand">{formatPrice(total)}</span>
                                </div>
                            </div>
                            <Link
                                to="/checkout"
                                data-testid="checkout-btn"
                                className="block w-full text-center bg-cp-secondary hover:bg-cp-secondary-hover text-white py-3.5 rounded-xl font-semibold transition-all active:scale-95"
                            >
                                Finalizar Compra
                            </Link>
                            <Link
                                to="/productos"
                                className="block text-center text-cp-secondary text-sm font-medium mt-3 hover:underline"
                            >
                                <ArrowLeft size={14} className="inline mr-1" />Seguir comprando
                            </Link>
                        </div>
                    </div>
                </div>
            </div>
        </>
    );
}
